import { EventDispatcher, Mesh, MeshBasicMaterial, PlaneGeometry } from 'three';
import { IDXFTextInfo } from '../types';
import TextCanvasTexture from './TextCanvasTexture';
import { globalStores } from '@/editor/stores';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { debounce } from 'lodash-es';
import { pixelRatio2d } from '@/editor/math/const';

export default class TextCanvasMesh extends Mesh {
  private _canvasTextureRenderer: TextCanvasTexture;
  private _scale: number = 1;
  private _isDisposed: boolean = false;
  private debounceRefresh: () => void;
  private canvasPixelSize: number = 2048;

  constructor() {
    super();
    this._canvasTextureRenderer = new TextCanvasTexture();
    this.geometry = new PlaneGeometry(1, 1);
    this.material = new MeshBasicMaterial({
      map: this._canvasTextureRenderer.getTexture(),
      // color: 0xff0000,
      // opacity: 0.5,
      transparent: true
    });

    this.debounceRefresh = debounce(() => {
      this.refresh();
    }, 150);
    editorEvent.on(EditorEventName.VIEW2D_CHANGE, this.debounceRefresh);

    this.material.addEventListener('dispose', this._onDispose);
  }

  setScale(scale: number) {
    this._scale = scale;
  }

  setTextList(textList: IDXFTextInfo[]) {
    this._canvasTextureRenderer.setTextList(textList);
  }

  setHighlightLayer(layerName: string | null) {
    this._canvasTextureRenderer.setHighlightLayer(layerName);
  }

  setTextColors(defaultColor: string, highlightColor: string) {
    this._canvasTextureRenderer.setTextColors(defaultColor, highlightColor);
  }

  private _prevCanvasGmtSize: number = 0;
  refresh() {
    if (this._isDisposed) return;
    const camera = globalStores.editorStateStore.sceneInfo.scene2d.camera;
    const center = camera.position.clone();
    const cameraToLocalPositionMatrix = this.matrixWorld.clone().invert().premultiply(this.matrix);
    center.applyMatrix4(cameraToLocalPositionMatrix);

    const canvasGmtSize =
      ((Math.max(camera.right - camera.left, camera.top - camera.bottom) / this._scale) * pixelRatio2d) / camera.zoom;

    const canvasRenderer = this._canvasTextureRenderer;

    canvasRenderer.setCanvasBounding({ x: center.x, y: center.y }, canvasGmtSize, this.canvasPixelSize);
    canvasRenderer.renderTextToCanvas();

    // 修改 plane geometry 的大小
    if (canvasGmtSize !== this._prevCanvasGmtSize) {
      this._prevCanvasGmtSize = canvasGmtSize;
      const positionArray = this.geometry.attributes.position.array;
      for (let i = 0; i < positionArray.length; i++) {
        positionArray[i] = 0.5 * canvasGmtSize * Math.sign(positionArray[i]);
      }
      this.geometry.attributes.position.needsUpdate = true;
    }

    this.position.set(center.x, center.y, 0);
  }

  private _onDispose = () => {
    this._isDisposed = true;
    editorEvent.off(EditorEventName.VIEW2D_CHANGE, this.debounceRefresh);
    this._canvasTextureRenderer.dispose();
    if (this.material instanceof EventDispatcher) {
      this.material.removeEventListener('dispose', this._onDispose);
    }
  };
}
